import { useSelector } from 'react-redux';
import { NameFieldsWrapper } from '../CustomerCard.styles';
import { TextInput } from '@interstate/components/TextInput';
import { NumericInput } from '@interstate/components/NumericInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { Dropdown } from '@makemydeal/dr-shared-components';
import { generateTestId } from '../utils/CustomerCardUtils';
import { useMemo } from 'react';
import { StateCodes, getStateCodesSelectInputValues } from '@makemydeal/dr-shared-ui-utils';
import type { VIEW_MODE } from '../CustomerCardEnums';
import type { AddressData } from './CustomerAddressTypes';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';

type AddressFormData = AddressData & {
    yearsAtAddress?: string;
    monthsAtAddress?: string;
};
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

type CustomerAddressFormProps = {
    formErrors: { [key: string]: { hasError: boolean; errorMessage: string } };
    values: AddressFormData;
    handleInputChange: (field: string, value: string) => void;
    viewMode: VIEW_MODE;
    disabled?: boolean;
    showTimeAtAddressFields?: boolean;
    isPostalCodeRequired?: boolean;
};

export const CustomerAddressForm = ({
    formErrors,
    values,
    handleInputChange,
    viewMode,
    disabled,
    showTimeAtAddressFields = false,
    isPostalCodeRequired = true
}: CustomerAddressFormProps) => {
    const stateSelectInputValues = useMemo(getStateCodesSelectInputValues, StateCodes);
    const isOptionalDesking = useSelector(dealModeSelectors.isOptionalDeskingMode);
    const isCreditAppFieldsCoverageEnabled = useSelector(featureToggleSelectors.isCreditAppFieldsCoverageEnabled);
    const requiredLabel = (label: string) => (isOptionalDesking ? `${label} (required)` : label);
    const piiInputDataAttr = { textInput: { dataAttr: { className: 'pii' } } };
    const isZipRequired = isPostalCodeRequired ? !isOptionalDesking : false;
    const zipLabel = isPostalCodeRequired ? requiredLabel('Postal Code') : 'Postal Code';

    return (
        <>
            <NameFieldsWrapper columns={isCreditAppFieldsCoverageEnabled ? 2 : 1}>
                <TextInput
                    name="streetAddress"
                    hasError={formErrors.streetAddress.hasError}
                    displayLabel={true}
                    label="Street Address"
                    value={values.streetAddress}
                    onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                    errorMessage={formErrors.streetAddress.errorMessage}
                    data-testid="customer-form-street-1-address"
                    size="small"
                    disabled={disabled}
                    slotProps={piiInputDataAttr}
                />

                <TextInput
                    name="streetAddress2"
                    hasError={formErrors.streetAddress2.hasError}
                    displayLabel={true}
                    label="Street Address 2"
                    value={values.streetAddress2}
                    onChange={(e) => handleInputChange('streetAddress2', e.target.value)}
                    errorMessage={formErrors.streetAddress2.errorMessage}
                    data-testid="customer-form-street-2-address"
                    size="small"
                    disabled={disabled}
                    slotProps={piiInputDataAttr}
                />
            </NameFieldsWrapper>

            <NameFieldsWrapper columns={isCreditAppFieldsCoverageEnabled ? 4 : 3}>
                <TextInput
                    name="city"
                    hasError={formErrors.city.hasError}
                    displayLabel={true}
                    label="City"
                    value={values.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    errorMessage={formErrors.city.errorMessage}
                    data-testid="customer-form-city"
                    size="small"
                    disabled={disabled}
                    slotProps={piiInputDataAttr}
                />

                <Dropdown
                    name="state"
                    displayLabel={true}
                    label="State"
                    value={values.state}
                    onChange={(e) => handleInputChange('state', e.target.value || '')}
                    options={stateSelectInputValues}
                    data-testid="customer-form-state"
                    size="small"
                    allowRemoval={true}
                    disabled={disabled}
                />

                <TextInput
                    name="zip"
                    hasError={formErrors.zip.hasError}
                    displayLabel={true}
                    label={zipLabel}
                    value={values.zip}
                    onChange={(e) => handleInputChange('zip', e.target.value)}
                    errorMessage={formErrors.zip.errorMessage}
                    required={isZipRequired}
                    data-testid={generateTestId('postal-code', viewMode)}
                    size="small"
                    disabled={disabled}
                    slotProps={piiInputDataAttr}
                />

                <TextInput
                    name="county"
                    hasError={formErrors.county.hasError}
                    displayLabel={true}
                    label="County"
                    value={values.county}
                    onChange={(e) => handleInputChange('county', e.target.value)}
                    errorMessage={formErrors.county.errorMessage}
                    data-testid="customer-form-county"
                    size="small"
                    disabled={disabled}
                    slotProps={piiInputDataAttr}
                />

                {showTimeAtAddressFields && (
                    <>
                        <NumericInput
                            name="yearsAtAddress"
                            displayLabel={true}
                            label="Yrs. at Address"
                            value={values.yearsAtAddress ?? ''}
                            allowDecimal={false}
                            onChange={(e: InterstateOnChangeEvent<string>) => handleInputChange('yearsAtAddress', e.target.value)}
                            hasError={Boolean(formErrors.yearsAtAddress?.hasError)}
                            errorMessage={formErrors.yearsAtAddress?.errorMessage || ''}
                            data-testid="customer-form-years-at-address"
                            size="small"
                            disabled={disabled}
                        />
                        <NumericInput
                            name="monthsAtAddress"
                            displayLabel={true}
                            label="Mos. at Address"
                            value={values.monthsAtAddress ?? ''}
                            allowDecimal={false}
                            onChange={(e: InterstateOnChangeEvent<string>) => handleInputChange('monthsAtAddress', e.target.value)}
                            hasError={Boolean(formErrors.monthsAtAddress?.hasError)}
                            errorMessage={formErrors.monthsAtAddress?.errorMessage || ''}
                            data-testid="customer-form-months-at-address"
                            size="small"
                            disabled={disabled}
                        />
                    </>
                )}
            </NameFieldsWrapper>
        </>
    );
};
